/**
 * Quarterly plan-switch offer modal (experiment `plan-switch-nudge-v1`).
 *
 * Opened by the renewal-anchored email/SMS deep link:
 *   `/plan-offer/<token>` → fresh magic link → `/app/account?planOffer=<id>`
 *
 * The pitch is the same as the post-purchase quarterly step, moved to the
 * days before a renewal: keep the treatment identical, have the renewal bill
 * quarterly at the standard quarterly rate, save the difference every month.
 * Acceptance rides the normal change-plan endpoint, so the current cycle is
 * untouched and the switch lands on the renewal the message was about.
 *
 * Offers go stale — a patient who cancelled, paused, or already switched
 * since the send gets an explanation instead of a button that would 409
 * (`unavailable_reason` off the offer payload).
 */

import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { Button } from '@/design-system/primitives';
import { useChangeSubscriptionPlan } from '@/lib/api/hooks';
import {
  usePlanOffer,
  usePlanOfferEvent,
  type PlanOffer,
} from '@/lib/api/planOfferHooks';
import { track } from '@/lib/analytics';
import { cn } from '@/lib/cn';
import type { ApiError } from '@/lib/api/client';

function fmtDollars(cents: number): string {
  const v = cents / 100;
  return v % 1 === 0 ? `$${v}` : `$${v.toFixed(2)}`;
}

function fmtRenewalDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

const UNAVAILABLE_COPY: Record<string, string> = {
  cancelling:
    'This plan is set to cancel at the end of the current period, so the quarterly switch no longer applies. Reactivate it below and the offer comes back.',
  paused:
    'This plan is paused right now, so there is no renewal to switch. Resume it below and you can switch to quarterly from Subscriptions.',
  already_switched:
    'You are already set to renew quarterly — nothing else to do. Your next renewal bills at the quarterly rate.',
  not_monthly:
    'This plan is not on the monthly schedule any more, so the quarterly switch does not apply.',
};

function unavailableCopy(reason: string): string {
  return (
    UNAVAILABLE_COPY[reason] ??
    'This offer is no longer available on your plan. You can review or change your plan length anytime under Subscriptions.'
  );
}

export function PlanSwitchOfferModal({
  nudgeId,
  onClose,
}: {
  nudgeId: string;
  onClose: () => void;
}) {
  const { data: offer, isLoading, isError } = usePlanOffer(nudgeId);
  const stampEvent = usePlanOfferEvent();
  const changePlan = useChangeSubscriptionPlan();
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const viewedRef = useRef(false);

  // View event — once per mount, and only once we actually have an offer to
  // show, so `plan_switch_modal_viewed` means "saw the numbers".
  useEffect(() => {
    if (!offer || viewedRef.current) return;
    viewedRef.current = true;
    track('plan_switch_modal_viewed', {
      experiment_key: offer.experiment_key,
      variant_key: offer.variant_key,
      nudge_id: offer.nudge_id,
      product_id: offer.product_id,
      subscription_id: offer.subscription_id,
      savings_per_month_cents: offer.savings_per_month_cents,
      unavailable_reason: offer.unavailable_reason,
      source: 'renewal_nudge',
    });
    stampEvent.mutate({ nudgeId: offer.nudge_id, eventType: 'modal_viewed' });
    // stampEvent identity is stable enough for this one-shot effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer]);

  function handleDismiss() {
    if (offer && !accepted) {
      track('plan_switch_dismissed', {
        experiment_key: offer.experiment_key,
        variant_key: offer.variant_key,
        nudge_id: offer.nudge_id,
        product_id: offer.product_id,
        savings_per_month_cents: offer.savings_per_month_cents,
        source: 'renewal_nudge',
      });
      stampEvent.mutate({ nudgeId: offer.nudge_id, eventType: 'dismissed' });
    }
    onClose();
  }

  async function handleAccept() {
    if (!offer || changePlan.isPending) return;
    setError(null);
    try {
      await changePlan.mutateAsync({
        subId: offer.subscription_id,
        frequency: 'quarterly',
        source: 'renewal_nudge',
        experimentKey: offer.experiment_key ?? undefined,
        variantKey: offer.variant_key ?? undefined,
      });
      track('plan_switch_accepted', {
        experiment_key: offer.experiment_key,
        variant_key: offer.variant_key,
        nudge_id: offer.nudge_id,
        product_id: offer.product_id,
        subscription_id: offer.subscription_id,
        savings_per_month_cents: offer.savings_per_month_cents,
        monthly_renewal_cents: offer.monthly_renewal_cents,
        quarterly_renewal_cents: offer.quarterly_renewal_cents,
        source: 'renewal_nudge',
      });
      setAccepted(true);
    } catch (e) {
      const apiErr = e as ApiError;
      setError(
        apiErr?.message ||
          'Could not switch your plan just now. You can also do this anytime under Subscriptions.',
      );
    }
  }

  return (
    <OfferShell accepted={accepted} onDismiss={handleDismiss}>
      {isLoading && (
        <div className="p-20 md:p-30">
          <div className="h-[12rem] bg-sunken animate-pulse" />
        </div>
      )}

      {isError && (
        <OfferMessage
          title="Offer unavailable"
          body="We couldn't load this offer. You can review your plan length anytime under Subscriptions."
          onClose={handleDismiss}
        />
      )}

      {offer && !isLoading && !isError && (
        <OfferBody
          offer={offer}
          accepted={accepted}
          accepting={changePlan.isPending}
          error={error}
          onAccept={handleAccept}
          onDismiss={handleDismiss}
        />
      )}
    </OfferShell>
  );
}

/** Dialog chrome — shared by the real modal and the dev preview so the
 *  preview can't drift from what patients see. */
function OfferShell({
  accepted,
  onDismiss,
  children,
}: {
  accepted: boolean;
  onDismiss: () => void;
  children: React.ReactNode;
}) {
  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-dialog bg-black/20 backdrop-blur-[10px]" />
        <Dialog.Content
          className={cn(
            'fixed z-dialog left-1/2 top-1/2 w-[calc(100vw-30px)] max-w-[46rem]',
            '-translate-x-1/2 -translate-y-1/2 bg-page border border-line-subtle',
            'max-h-[calc(100vh-60px)] overflow-y-auto shadow-modal focus-visible:outline-none',
          )}
        >
          <div className="flex items-center justify-between border-b border-line-subtle bg-sunken">
            <div className="type-label-sm text-ink-secondary px-15 py-15">
              {accepted ? 'QUARTERLY CONFIRMED' : 'YOUR RENEWAL'}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close offer"
                className="w-45 h-45 inline-flex items-center justify-center border-l border-line-subtle text-ink-secondary hover:text-ink"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Dev-only preview (`/preview/plan-offer`) so the offer can be iterated on
 * without a nudge row, a magic link, or a renewal in the window. Mirrors the
 * other `previewRoutes` entries in App.tsx — stripped from prod bundles.
 */
export function PlanSwitchOfferPreview() {
  const [accepted, setAccepted] = useState(false);
  const offer: PlanOffer = {
    nudge_id: 'preview',
    subscription_id: 'sub_preview',
    product_id: 'glutathione-injection-200mg-10ml',
    product_title: 'Glutathione Injection',
    monthly_renewal_cents: 14900,
    quarterly_renewal_cents: 39900,
    quarterly_per_month_cents: 13300,
    savings_per_month_cents: 1600,
    savings_per_quarter_cents: 4800,
    next_renewal_date: '2026-08-02',
    first_shipment_containers: 3,
    container_noun: 'vials',
    experiment_key: 'plan-switch-nudge-v1',
    variant_key: 'variant-a',
    unavailable_reason: null,
    switched: false,
  };
  return (
    <OfferShell accepted={accepted} onDismiss={() => setAccepted(false)}>
      <OfferBody
        offer={offer}
        accepted={accepted}
        accepting={false}
        error={null}
        onAccept={() => setAccepted(true)}
        onDismiss={() => setAccepted(false)}
      />
    </OfferShell>
  );
}

function OfferMessage({
  title,
  body,
  onClose,
}: {
  title: string;
  body: string;
  onClose: () => void;
}) {
  return (
    <div className="p-20 md:p-30 flex flex-col gap-20">
      <Dialog.Title className="type-title-lg text-ink">
        {title.toUpperCase()}
      </Dialog.Title>
      <Dialog.Description className="type-body-md text-ink-secondary">
        {body}
      </Dialog.Description>
      <Button type="button" variant="filled" className="w-full" onClick={onClose}>
        <span>Close</span>
      </Button>
    </div>
  );
}

function OfferBody({
  offer,
  accepted,
  accepting,
  error,
  onAccept,
  onDismiss,
}: {
  offer: PlanOffer;
  accepted: boolean;
  accepting: boolean;
  error: string | null;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  const renewalDate = fmtRenewalDate(offer.next_renewal_date);
  const savings = fmtDollars(offer.savings_per_month_cents);
  const monthly = fmtDollars(offer.monthly_renewal_cents);
  const quarterlyTotal = fmtDollars(offer.quarterly_renewal_cents);
  const quarterlyMo = fmtDollars(offer.quarterly_per_month_cents);
  const showContainers =
    offer.first_shipment_containers != null && offer.first_shipment_containers > 1;

  if (accepted) {
    return (
      <div className="flex flex-col">
        <div className="bg-sunken flex flex-col gap-8 px-20 md:px-30 py-25">
          <Dialog.Title className="type-title-lg text-ink">
            LOCKED IN — QUARTERLY SAVINGS
          </Dialog.Title>
          <Dialog.Description className="type-body-md text-ink-secondary">
            Nothing changes this cycle.{' '}
            {renewalDate ? `On ${renewalDate}` : 'At your next renewal'}, your{' '}
            {offer.product_title} plan renews quarterly at {quarterlyTotal} every
            3 months ({quarterlyMo}/mo) — saving you {savings} every month
            {showContainers
              ? `, with ${offer.first_shipment_containers} ${
                  offer.container_noun ?? 'units'
                } shipping up front`
              : ''}
            .
          </Dialog.Description>
        </div>
        <div className="p-20 md:p-30">
          <Button
            type="button"
            variant="filled"
            className="w-full"
            onClick={onDismiss}
          >
            <span>Done</span>
          </Button>
        </div>
      </div>
    );
  }

  if (offer.unavailable_reason) {
    return (
      <OfferMessage
        title={
          offer.unavailable_reason === 'already_switched'
            ? 'Already on quarterly'
            : 'Offer no longer applies'
        }
        body={unavailableCopy(offer.unavailable_reason)}
        onClose={onDismiss}
      />
    );
  }

  return (
    <div className="flex flex-col">
      <div className="bg-sunken flex flex-col gap-8 px-20 md:px-30 py-25">
        <Dialog.Title className="type-title-lg text-ink">
          {`SAVE ${savings}/MO ON ${offer.product_title.toUpperCase()}`}
        </Dialog.Title>
        <Dialog.Description className="type-body-md text-ink-secondary">
          {renewalDate
            ? `Your plan renews on ${renewalDate}.`
            : 'Your plan renews in a few days.'}{' '}
          Switch that renewal to quarterly and pay {quarterlyMo}/mo instead of{' '}
          {monthly}/mo — same treatment, same dose, billed {quarterlyTotal} every
          3 months.
        </Dialog.Description>
      </div>

      {/* Both prices are labelled as NEXT-RENEWAL prices: the patient's first
          month was an intro price, so an unlabelled comparison reads as a
          price hike. */}
      <div className="flex flex-col border-b border-line-subtle">
        <div className="bg-[#F0F0F0] border-y border-line-subtle px-20 md:px-30 py-8 type-label-xs text-ink-muted">
          {renewalDate ? `YOUR RENEWAL ON ${renewalDate.toUpperCase()}` : 'AT YOUR NEXT RENEWAL'}
        </div>
        <div className="flex items-center justify-between px-20 md:px-30 py-14 border-b border-line-subtle">
          <div className="flex flex-col gap-3">
            <span className="type-label-sm text-ink">MONTHLY</span>
            <span className="type-label-xs text-ink-muted">CURRENT PLAN</span>
          </div>
          <span className="type-body-md text-ink">{monthly}/mo</span>
        </div>
        <div className="flex items-center justify-between px-20 md:px-30 py-14 bg-[#FAFAFA]">
          <div className="flex flex-col gap-3">
            <span className="type-label-sm text-ink">QUARTERLY</span>
            <span className="type-label-xs text-ink-muted">
              {quarterlyTotal.toUpperCase()} EVERY 3 MONTHS
            </span>
          </div>
          <div className="flex flex-col items-end gap-3">
            <span className="type-body-md text-ink">{quarterlyMo}/mo</span>
            <span className="type-label-xs text-[#0A7A3D]">
              SAVE {savings}/MO
            </span>
          </div>
        </div>
      </div>

      {showContainers && (
        <div className="flex items-start gap-8 px-20 md:px-30 py-12 border-b border-line-subtle">
          <span className="type-body-md text-ink leading-tight">+</span>
          <p className="type-label-xs text-ink-secondary leading-130 m-0">
            {offer.first_shipment_containers} {(offer.container_noun ?? 'units').toUpperCase()}{' '}
            SHIP UP FRONT ON YOUR NEXT ORDER
          </p>
        </div>
      )}

      <div className="flex flex-col gap-10 p-20 md:p-30">
        <Button
          type="button"
          variant="filled"
          className="w-full"
          disabled={accepting}
          onClick={onAccept}
        >
          <span>
            {accepting ? 'Switching…' : `Switch and save ${savings}/mo`}
          </span>
        </Button>
        <button
          type="button"
          onClick={onDismiss}
          disabled={accepting}
          className="type-label-xs text-ink-muted uppercase underline py-6"
        >
          No thanks, keep monthly
        </button>
        {error && (
          <p className="type-body-sm text-[#B3261E] leading-130 m-0">{error}</p>
        )}
        {/* The load-bearing disclosures, deliberately quiet: standard (not
            intro) quarterly price, applies at renewal, cancellable first. */}
        <p className="type-body-sm text-ink-muted leading-140 m-0">
          Applies at your next renewal{renewalDate ? ` on ${renewalDate}` : ''} —
          your current cycle doesn&rsquo;t change, and you&rsquo;ll be billed{' '}
          {quarterlyTotal} every 3 months at the standard quarterly rate. You can
          switch back or cancel anytime before renewal under Subscriptions.
        </p>
      </div>
    </div>
  );
}
